const VIEWPORT_SCROLL_LOCK_CLASS_NAME = 'viewport-scroll-locked';
const VISUAL_VIEWPORT_HEIGHT_PROPERTY = '--visual-viewport-height';
let activeViewportScrollLocks = 0;
let activeVisualViewportTrackers = 0;
let activeFocusedControlRevealTrackers = 0;
let stopVisualViewportTracking: (() => void) | null = null;

interface VisualViewportTrackingOptions {
  revealFocusedControl?: boolean;
}

function startVisualViewportTracking(): () => void {
  const visualViewport = window.visualViewport;
  let focusFrame: number | null = null;

  const updateViewportHeight = () => {
    const height = visualViewport?.height ?? window.innerHeight;
    document.documentElement.style.setProperty(VISUAL_VIEWPORT_HEIGHT_PROPERTY, `${Math.round(height)}px`);
  };
  const revealFocusedElement = () => {
    updateViewportHeight();
    if (focusFrame !== null) {
      window.cancelAnimationFrame(focusFrame);
    }
    focusFrame = window.requestAnimationFrame(() => {
      focusFrame = null;
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement
        && activeElement.matches('input, textarea, select, [contenteditable="true"]')
      ) {
        activeElement.scrollIntoView({ block: 'nearest' });
      }
    });
  };
  const handleVisualViewportResize = () => {
    if (activeFocusedControlRevealTrackers > 0) {
      revealFocusedElement();
      return;
    }

    updateViewportHeight();
  };

  updateViewportHeight();
  window.addEventListener('resize', updateViewportHeight);
  visualViewport?.addEventListener('resize', handleVisualViewportResize);
  visualViewport?.addEventListener('scroll', updateViewportHeight);

  return () => {
    if (focusFrame !== null) {
      window.cancelAnimationFrame(focusFrame);
    }
    window.removeEventListener('resize', updateViewportHeight);
    visualViewport?.removeEventListener('resize', handleVisualViewportResize);
    visualViewport?.removeEventListener('scroll', updateViewportHeight);
    document.documentElement.style.removeProperty(VISUAL_VIEWPORT_HEIGHT_PROPERTY);
  };
}

/** Tracks the usable viewport height without preventing native page overscroll. */
export function acquireVisualViewportTracking(options: VisualViewportTrackingOptions = {}): () => void {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  const shouldRevealFocusedControl = options.revealFocusedControl === true;
  activeVisualViewportTrackers += 1;
  if (shouldRevealFocusedControl) {
    activeFocusedControlRevealTrackers += 1;
  }
  if (activeVisualViewportTrackers === 1) {
    stopVisualViewportTracking = startVisualViewportTracking();
  }
  let isReleased = false;

  return () => {
    if (isReleased) {
      return;
    }

    isReleased = true;
    activeVisualViewportTrackers = Math.max(0, activeVisualViewportTrackers - 1);
    if (shouldRevealFocusedControl) {
      activeFocusedControlRevealTrackers = Math.max(0, activeFocusedControlRevealTrackers - 1);
    }
    if (activeVisualViewportTrackers === 0) {
      stopVisualViewportTracking?.();
      stopVisualViewportTracking = null;
    }
  };
}

/** Locks page scrolling until the returned idempotent release callback runs. */
export function acquireViewportScrollLock(): () => void {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  const releaseVisualViewportTracking = acquireVisualViewportTracking({ revealFocusedControl: true });
  activeViewportScrollLocks += 1;
  document.documentElement.classList.add(VIEWPORT_SCROLL_LOCK_CLASS_NAME);
  let isReleased = false;

  return () => {
    if (isReleased) {
      return;
    }

    isReleased = true;
    activeViewportScrollLocks = Math.max(0, activeViewportScrollLocks - 1);
    if (activeViewportScrollLocks === 0) {
      document.documentElement.classList.remove(VIEWPORT_SCROLL_LOCK_CLASS_NAME);
    }
    releaseVisualViewportTracking();
  };
}
